import React, { useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { format, isValid, parseISO, startOfMonth, startOfWeek, subDays } from 'date-fns';
import { tr } from 'date-fns/locale';
import api from '../../api/axiosConfig';
import './ConcreteReportDashboard.css';

const COLORS = ['#2563eb', '#0d9488', '#f59e0b', '#8b5cf6', '#e879f9', '#64748b'];
const numberFormatter = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 2 });
const compactFormatter = new Intl.NumberFormat('tr-TR', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

function toNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function sumVolumes(items) {
  return items.reduce((total, item) => total + toNumber(item.volume), 0);
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function formatVolume(value) {
  return numberFormatter.format(toNumber(value));
}

function parseDate(value) {
  if (!value) return null;
  const date = parseISO(String(value));
  return isValid(date) ? date : null;
}

function formatPeriod(value, period = 'daily') {
  const date = parseDate(value);
  if (!date) return value || '—';

  if (period === 'monthly') return format(date, 'MMM yyyy', { locale: tr });
  if (period === 'weekly') return `${format(date, 'd MMM', { locale: tr })} haftası`;
  return format(date, 'd MMM', { locale: tr });
}

function shortText(value, limit = 18) {
  const text = String(value ?? '—');
  return text.length > limit ? `${text.slice(0, limit - 1)}…` : text;
}

function Icon({ name, size = 20, strokeWidth = 1.8 }) {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
  };

  switch (name) {
    case 'chart':
      return <svg {...common}><path d="M3 3v18h18" /><path d="m7 16 4-5 4 3 5-7" /></svg>;
    case 'layers':
      return <svg {...common}><path d="m12 2 9 5-9 5-9-5 9-5Z" /><path d="m3 12 9 5 9-5" /><path d="m3 17 9 5 9-5" /></svg>;
    case 'building':
      return <svg {...common}><rect x="4" y="3" width="16" height="18" rx="2" /><path d="M9 21v-4h6v4M8 7h2m4 0h2M8 11h2m4 0h2" /></svg>;
    case 'truck':
      return <svg {...common}><path d="M3 5h11v12H3zM14 9h4l3 4v4h-7z" /><circle cx="7.5" cy="18" r="2" /><circle cx="17.5" cy="18" r="2" /></svg>;
    case 'calendar':
      return <svg {...common}><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M7 3v4m10-4v4M3 10h18" /></svg>;
    case 'refresh':
      return <svg {...common}><path d="M20 7v5h-5" /><path d="M4 17v-5h5" /><path d="M6 9a7 7 0 0 1 12-2l2 5M4 12l2 5a7 7 0 0 0 12-2" /></svg>;
    case 'clock':
      return <svg {...common}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>;
    case 'filter':
      return <svg {...common}><path d="M4 7h16M7 12h10m-7 5h4" /></svg>;
    case 'trend':
      return <svg {...common}><path d="m3 17 6-6 4 4 8-8M15 7h6v6" /></svg>;
    case 'warning':
      return <svg {...common}><path d="m12 3 10 18H2L12 3Z" /><path d="M12 9v4m0 4h.01" /></svg>;
    case 'empty':
      return <svg {...common}><rect x="4" y="4" width="16" height="16" rx="3" /><path d="M8 15h8M8 11h5" /></svg>;
    default:
      return null;
  }
}

function ChartCard({ title, subtitle, icon, right, children, className = '' }) {
  return (
    <section className={`cr-card ${className}`}>
      <div className="cr-card-header">
        <div className="cr-card-heading">
          <span className="cr-card-icon"><Icon name={icon} size={18} /></span>
          <div className="cr-card-heading-text">
            <h2>{title}</h2>
            <p>{subtitle}</p>
          </div>
        </div>
        {right && <div className="cr-card-action">{right}</div>}
      </div>
      {children}
    </section>
  );
}

function StatCard({ icon, variant, label, value, suffix, description, loading }) {
  return (
    <div className={`cr-stat cr-stat--${variant}`}>
      <div className="cr-stat-top">
        <span className="cr-stat-icon"><Icon name={icon} size={21} /></span>
        <span className="cr-stat-indicator" aria-hidden="true" />
      </div>
      <div className="cr-stat-label">{label}</div>
      {loading ? (
        <div className="cr-skeleton cr-skeleton--value" />
      ) : (
        <div className="cr-stat-value">
          {value}<span className="cr-stat-unit">{suffix}</span>
        </div>
      )}
      <p className="cr-stat-description">{description}</p>
    </div>
  );
}

function NoData({ message = 'Seçilen tarih aralığında gösterilecek veri bulunamadı.' }) {
  return (
    <div className="cr-empty">
      <span className="cr-empty-icon"><Icon name="empty" size={25} /></span>
      <strong>Henüz veri bulunmuyor</strong>
      <span>{message}</span>
    </div>
  );
}

const tooltipProps = {
  cursor: { fill: 'rgba(37, 99, 235, 0.045)' },
  contentStyle: {
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    boxShadow: '0 14px 35px rgba(15, 23, 42, 0.10)',
    padding: '11px 14px',
    fontSize: 12,
  },
  itemStyle: { color: '#0f172a', fontWeight: 600 },
};

function HorizontalVolumeChart({ items, color, emptyMessage }) {
  if (!items.length) return <NoData message={emptyMessage} />;

  const height = Math.max(250, items.length * 44 + 34);

  return (
    <div className="cr-bar-scroll">
      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={items} layout="vertical" margin={{ top: 6, right: 22, bottom: 4, left: 0 }} barCategoryGap="32%">
            <CartesianGrid stroke="#e8edf4" horizontal={false} strokeDasharray="3 5" />
            <XAxis
              type="number"
              tickLine={false}
              axisLine={false}
              tick={{ fill: '#94a3b8', fontSize: 11 }}
              tickFormatter={(value) => compactFormatter.format(value)}
              allowDecimals={false}
            />
            <YAxis
              dataKey="name"
              type="category"
              width={136}
              tickLine={false}
              axisLine={false}
              tick={{ fill: '#475569', fontSize: 11, fontWeight: 500 }}
              tickFormatter={(name) => shortText(name, 19)}
              interval={0}
            />
            <Tooltip
              {...tooltipProps}
              formatter={(value) => [`${formatVolume(value)} m³`, 'Hacim']}
              labelFormatter={(label) => label}
            />
            <Bar dataKey="volume" name="Hacim" fill={color} radius={[0, 5, 5, 0]} maxBarSize={20} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

const PERIOD_OPTIONS = [
  { value: 'daily', label: 'Günlük' },
  { value: 'weekly', label: 'Haftalık' },
  { value: 'monthly', label: 'Aylık' },
];

const DATE_PRESETS = [
  { value: 'all', label: 'Tümü' },
  { value: '7d', label: 'Son 7 gün' },
  { value: '30d', label: 'Son 30 gün' },
  { value: '90d', label: 'Son 90 gün' },
];

export default function ConcreteReportDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeFilter, setTimeFilter] = useState('daily');
  const [activePreset, setActivePreset] = useState('all');
  const [dates, setDates] = useState({ start: '', end: '' });
  const [refreshKey, setRefreshKey] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(null);

  const invalidRange = Boolean(dates.start && dates.end && dates.start > dates.end);

  useEffect(() => {
    if (invalidRange) {
      setLoading(false);
      return undefined;
    }

    const controller = new AbortController();

    async function fetchData() {
      setLoading(true);
      setError('');

      try {
        const params = {};
        if (dates.start) params.start_date = dates.start;
        if (dates.end) params.end_date = dates.end;

        // Mevcut backend endpoint'i ve parametre isimleri değiştirilmedi.
        const response = await api.get('/records/concrete-summary', {
          params,
          signal: controller.signal,
        });

        if (!controller.signal.aborted) {
          setData(response.data ?? {});
          setLastUpdated(new Date());
        }
      } catch (requestError) {
        if (controller.signal.aborted) return;
        console.error('Beton raporu yükleme hatası:', requestError);
        setError('Rapor verileri alınamadı. Bağlantıyı kontrol edip yeniden deneyin.');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }

    fetchData();
    return () => controller.abort();
  }, [dates.start, dates.end, invalidRange, refreshKey]);

  const timeline = useMemo(() => safeArray(data?.timeline), [data]);
  const byDeliveryType = useMemo(() => safeArray(data?.by_delivery_type), [data]);
  const byConcreteType = useMemo(() => safeArray(data?.by_concrete_type), [data]);
  const byCompany = useMemo(() => safeArray(data?.by_company), [data]);
  const bySite = useMemo(() => safeArray(data?.by_site), [data]);

  const groupedTimeline = useMemo(() => {
    const grouped = new Map();

    timeline.forEach((item) => {
      const date = parseDate(item?.date);
      if (!date) return;

      let key = format(date, 'yyyy-MM-dd');
      if (timeFilter === 'weekly') key = format(startOfWeek(date, { weekStartsOn: 1 }), 'yyyy-MM-dd');
      if (timeFilter === 'monthly') key = format(startOfMonth(date), 'yyyy-MM-dd');

      grouped.set(key, (grouped.get(key) || 0) + toNumber(item.volume));
    });

    return [...grouped.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, volume]) => ({ date, volume: Number(volume.toFixed(2)) }));
  }, [timeline, timeFilter]);

  const chartData = useMemo(() => ({
    byDeliveryType: byDeliveryType.map((item) => ({
      name: String(item.name ?? 'Belirtilmemiş'),
      volume: toNumber(item.volume),
    })),
    byConcreteType: byConcreteType.map((item) => ({
      name: String(item.name ?? 'Belirtilmemiş'),
      volume: toNumber(item.volume),
    })).sort((a, b) => b.volume - a.volume),
    byCompany: byCompany.map((item) => ({
      name: String(item.name ?? 'Belirtilmemiş'),
      volume: toNumber(item.volume),
    })).sort((a, b) => b.volume - a.volume),
    bySite: bySite.map((item) => ({
      name: String(item.name ?? 'Belirtilmemiş'),
      volume: toNumber(item.volume),
    })).sort((a, b) => b.volume - a.volume),
  }), [byDeliveryType, byConcreteType, byCompany, bySite]);

  const metrics = useMemo(() => {
    const totalFromTimeline = sumVolumes(timeline);
    const totalFromTypes = sumVolumes(byDeliveryType);

    return {
      total: timeline.length ? totalFromTimeline : totalFromTypes,
      internal: sumVolumes(bySite),
      external: sumVolumes(byCompany),
      activeDays: new Set(timeline.map((item) => String(item?.date || '').slice(0, 10)).filter(Boolean)).size,
    };
  }, [timeline, byDeliveryType, bySite, byCompany]);

  const deliveryTotal = sumVolumes(chartData.byDeliveryType);

  function setDatePreset(preset) {
    setActivePreset(preset);
    if (preset === 'all') {
      setDates({ start: '', end: '' });
      return;
    }

    const count = Number(preset.replace('d', ''));
    const today = new Date();
    setDates({
      start: format(subDays(today, count - 1), 'yyyy-MM-dd'),
      end: format(today, 'yyyy-MM-dd'),
    });
  }

  function updateDate(field, value) {
    setActivePreset('custom');
    setDates((previous) => ({ ...previous, [field]: value }));
  }

  return (
    <main className="cr-page">
      <div className="cr-container">
        <header className="cr-hero">
          <div className="cr-hero-main">
            <span className="cr-hero-icon"><Icon name="chart" size={25} /></span>
            <div>
              <div className="cr-eyebrow"><span className="cr-eyebrow-dot" /> OPERASYON RAPORLARI <span className="cr-eyebrow-divider">/</span> BETON</div>
              <h1>Beton Raporlama Paneli</h1>
              <p>Döküm hacimleri, satış dağılımları ve şantiye performansını tek ekrandan takip edin.</p>
            </div>
          </div>
          <div className="cr-hero-actions">
            {lastUpdated && <span className="cr-updated"><Icon name="clock" size={14} /> Son güncelleme: {format(lastUpdated, 'HH:mm')}</span>}
            <button
              type="button"
              className="cr-refresh-button"
              onClick={() => setRefreshKey((value) => value + 1)}
              disabled={loading || invalidRange}
              aria-label="Raporu yenile"
            >
              <Icon name="refresh" size={17} />
              <span>{loading ? 'Yükleniyor' : 'Yenile'}</span>
            </button>
          </div>
        </header>

        <section className="cr-filter-panel" aria-label="Rapor filtreleri">
          <div className="cr-filter-topline">
            <div className="cr-filter-label"><Icon name="filter" size={17} /> Rapor dönemi</div>
            <div className="cr-quick-filters" role="group" aria-label="Hızlı tarih aralığı">
              {DATE_PRESETS.map((preset) => (
                <button
                  key={preset.value}
                  type="button"
                  className={`cr-quick-button ${activePreset === preset.value ? 'is-active' : ''}`}
                  onClick={() => setDatePreset(preset.value)}
                  aria-pressed={activePreset === preset.value}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          <div className="cr-date-row">
            <label className="cr-date-field" htmlFor="cr-start-date">
              <span>Başlangıç tarihi</span>
              <input
                id="cr-start-date"
                type="date"
                value={dates.start}
                max={dates.end || undefined}
                onChange={(event) => updateDate('start', event.target.value)}
              />
            </label>
            <span className="cr-date-separator" aria-hidden="true">—</span>
            <label className="cr-date-field" htmlFor="cr-end-date">
              <span>Bitiş tarihi</span>
              <input
                id="cr-end-date"
                type="date"
                value={dates.end}
                min={dates.start || undefined}
                onChange={(event) => updateDate('end', event.target.value)}
              />
            </label>
            <span className="cr-filter-note"><Icon name="calendar" size={15} /> Tarih seçimi tüm rapora uygulanır.</span>
            {(dates.start || dates.end) && (
              <button className="cr-clear-button" type="button" onClick={() => setDatePreset('all')}>Temizle</button>
            )}
          </div>
        </section>

        {invalidRange && (
          <div className="cr-alert" role="alert">
            <Icon name="warning" size={18} /> Bitiş tarihi başlangıç tarihinden önce olamaz.
          </div>
        )}
        {error && (
          <div className="cr-alert" role="alert">
            <Icon name="warning" size={18} />
            <span>{error}</span>
            <button type="button" onClick={() => setRefreshKey((value) => value + 1)}>Tekrar dene</button>
          </div>
        )}

        <div className="cr-section-heading">
          <div>
            <div className="cr-section-kicker">GENEL BAKIŞ</div>
            <h2>Beton operasyonları özeti</h2>
          </div>
          <span className="cr-section-caption">{activePreset === 'all' ? 'Tüm kayıtlar' : 'Seçili tarih aralığı'}</span>
        </div>

        <section className="cr-stats" aria-label="Özet istatistikler" aria-busy={loading}>
          <StatCard
            icon="layers"
            variant="blue"
            label="Toplam döküm hacmi"
            value={formatVolume(metrics.total)}
            suffix="m³"
            description="Seçili dönemdeki toplam üretim"
            loading={loading && !data}
          />
          <StatCard
            icon="building"
            variant="teal"
            label="İç satış hacmi"
            value={formatVolume(metrics.internal)}
            suffix="m³"
            description={`${bySite.length} şantiye kaydı`}
            loading={loading && !data}
          />
          <StatCard
            icon="truck"
            variant="amber"
            label="Dış satış hacmi"
            value={formatVolume(metrics.external)}
            suffix="m³"
            description={`${byCompany.length} firma kaydı`}
            loading={loading && !data}
          />
          <StatCard
            icon="calendar"
            variant="violet"
            label="Aktif döküm günü"
            value={numberFormatter.format(metrics.activeDays)}
            suffix="gün"
            description="Döküm kaydı bulunan gün sayısı"
            loading={loading && !data}
          />
        </section>

        <div className="cr-section-heading cr-section-heading--charts">
          <div>
            <div className="cr-section-kicker">GRAFİKSEL ANALİZ</div>
            <h2>Performans ve dağılım raporları</h2>
          </div>
          {loading && data && <span className="cr-inline-loading" role="status">Rapor güncelleniyor…</span>}
        </div>

        <div className="cr-chart-grid">
          <ChartCard
            className="cr-timeline-card"
            title="Zamana Göre Döküm"
            subtitle="Günlük, haftalık ve aylık beton hacmi eğilimi"
            icon="trend"
            right={
              <div className="cr-segmented" role="group" aria-label="Grafik zaman aralığı">
                {PERIOD_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={timeFilter === option.value ? 'is-active' : ''}
                    onClick={() => setTimeFilter(option.value)}
                    aria-pressed={timeFilter === option.value}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            }
          >
            {groupedTimeline.length ? (
              <div className="cr-line-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={groupedTimeline} margin={{ top: 20, right: 12, bottom: 6, left: -16 }}>
                    <CartesianGrid vertical={false} stroke="#e8edf4" strokeDasharray="4 5" />
                    <XAxis
                      dataKey="date"
                      tickLine={false}
                      axisLine={false}
                      minTickGap={26}
                      tick={{ fill: '#94a3b8', fontSize: 11 }}
                      tickFormatter={(value) => formatPeriod(value, timeFilter)}
                      dy={10}
                    />
                    <YAxis
                      tickLine={false}
                      axisLine={false}
                      tick={{ fill: '#94a3b8', fontSize: 11 }}
                      tickFormatter={(value) => compactFormatter.format(value)}
                      width={60}
                    />
                    <Tooltip
                      {...tooltipProps}
                      formatter={(value) => [`${formatVolume(value)} m³`, 'Döküm hacmi']}
                      labelFormatter={(label) => formatPeriod(label, timeFilter)}
                    />
                    <Line
                      type="monotone"
                      dataKey="volume"
                      name="Döküm hacmi"
                      stroke="#2563eb"
                      strokeWidth={3}
                      dot={groupedTimeline.length < 12 ? { r: 3, fill: '#fff', strokeWidth: 2 } : false}
                      activeDot={{ r: 6, fill: '#2563eb', stroke: '#fff', strokeWidth: 3 }}
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : <NoData />}
            <div className="cr-chart-foot"><span className="cr-legend-dot cr-legend-dot--blue" /> Döküm hacmi (m³) <span className="cr-chart-foot-right">{groupedTimeline.length} dönem</span></div>
          </ChartCard>

          <ChartCard
            className="cr-delivery-card"
            title="İç / Dış Satış Dağılımı"
            subtitle="Satış türlerine göre beton hacimleri"
            icon="layers"
          >
            {chartData.byDeliveryType.length && deliveryTotal > 0 ? (
              <div className="cr-donut-layout">
                <div className="cr-donut-wrap">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={chartData.byDeliveryType}
                        dataKey="volume"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius="72%"
                        outerRadius="93%"
                        paddingAngle={2}
                        stroke="none"
                        isAnimationActive={false}
                      >
                        {chartData.byDeliveryType.map((item, index) => (
                          <Cell key={`${item.name}-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        {...tooltipProps}
                        formatter={(value) => [`${formatVolume(value)} m³`, 'Hacim']}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="cr-donut-center" aria-hidden="true">
                    <span>Toplam hacim</span>
                    <strong>{compactFormatter.format(deliveryTotal)}</strong>
                    <small>m³</small>
                  </div>
                </div>
                <div className="cr-donut-legend">
                  {chartData.byDeliveryType.map((item, index) => {
                    const percentage = (item.volume / deliveryTotal) * 100;
                    return (
                      <div className="cr-donut-legend-row" key={`${item.name}-${index}`}>
                        <div className="cr-donut-legend-label">
                          <span className="cr-donut-color" style={{ background: COLORS[index % COLORS.length] }} />
                          <span title={item.name}>{item.name}</span>
                          <strong>%{numberFormatter.format(percentage)}</strong>
                        </div>
                        <div className="cr-progress-track">
                          <div style={{ width: `${Math.max(0, Math.min(percentage, 100))}%`, background: COLORS[index % COLORS.length] }} />
                        </div>
                        <div className="cr-donut-volume">{formatVolume(item.volume)} m³</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : <NoData />}
          </ChartCard>

          <ChartCard
            className="cr-types-card"
            title="Beton Cinsine Göre Hacimler"
            subtitle="Beton sınıflarının toplam döküm içindeki dağılımı"
            icon="layers"
            right={<span className="cr-count-pill">{chartData.byConcreteType.length} beton cinsi</span>}
          >
            <HorizontalVolumeChart
              items={chartData.byConcreteType}
              color="#2563eb"
              emptyMessage="Bu dönem için beton cinsi kaydı bulunamadı."
            />
          </ChartCard>

          <ChartCard
            className="cr-company-card"
            title="Firmalara Göre Dış Satış"
            subtitle="Müşteri firma bazında gerçekleşen satış hacmi"
            icon="truck"
            right={<span className="cr-count-pill">{chartData.byCompany.length} firma</span>}
          >
            <HorizontalVolumeChart
              items={chartData.byCompany}
              color="#f59e0b"
              emptyMessage="Bu dönem için dış satış kaydı bulunamadı."
            />
          </ChartCard>

          <ChartCard
            className="cr-site-card"
            title="Şantiyelere Göre İç Satış"
            subtitle="Şantiye bazında toplam beton tüketim hacmi"
            icon="building"
            right={<span className="cr-count-pill">{chartData.bySite.length} şantiye</span>}
          >
            <HorizontalVolumeChart
              items={chartData.bySite}
              color="#0d9488"
              emptyMessage="Bu dönem için iç satış kaydı bulunamadı."
            />
          </ChartCard>
        </div>

        <footer className="cr-footer">
          <span>Beton operasyonları · Raporlama ve analiz</span>
          <span>Hacim birimi: m³</span>
        </footer>
      </div>
    </main>
  );
}
